import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, MessageSquare, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";

const SUBJECTS = [
  { value: "bug", label_fr: "Signaler un bug", label_en: "Report a bug" },
  { value: "price", label_fr: "Prix incorrect", label_en: "Incorrect price" },
  { value: "feature", label_fr: "Suggestion de fonctionnalité", label_en: "Feature suggestion" },
  { value: "premium", label_fr: "Question sur le Premium", label_en: "Premium question" },
  { value: "rgpd", label_fr: "Demande RGPD / mes données", label_en: "GDPR / data request" },
  { value: "other", label_fr: "Autre", label_en: "Other" },
];

export default function Contact() {
  const { i18n } = useTranslation();
  const isFr = (i18n.language || "en").toLowerCase().startsWith("fr");

  const [form, setForm] = useState({ name: "", email: "", subject: "bug", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const t = isFr
    ? {
        eyebrow: "// CONTACT",
        title: "Une question, un bug, une idée ?",
        subtitle: "On répond généralement sous 48h. Pour les demandes RGPD, écrivez directement à privacy@cartonomie.fr.",
        name: "Votre nom",
        name_ph: "Ash Ketchum",
        email: "Votre e-mail",
        email_ph: "vous@exemple.com",
        subject: "Sujet",
        message: "Votre message",
        message_ph: "Décrivez votre demande aussi précisément que possible…",
        send: "Envoyer le message",
        sending: "Envoi…",
        success_title: "Message envoyé !",
        success_body: "Merci pour votre message. Nous reviendrons vers vous rapidement.",
        another: "Envoyer un autre message",
        error: "Impossible d'envoyer le message. Réessayez ou écrivez directement à contact@cartonomie.fr.",
        direct_email: "Ou écrivez-nous directement à",
      }
    : {
        eyebrow: "// CONTACT",
        title: "Question, bug or idea?",
        subtitle: "We usually reply within 48h. For GDPR requests, email privacy@cartonomie.fr directly.",
        name: "Your name",
        name_ph: "Ash Ketchum",
        email: "Your email",
        email_ph: "you@example.com",
        subject: "Subject",
        message: "Your message",
        message_ph: "Describe your request as precisely as possible…",
        send: "Send message",
        sending: "Sending…",
        success_title: "Message sent!",
        success_body: "Thanks for reaching out. We'll get back to you shortly.",
        another: "Send another message",
        error: "Could not send. Try again or email contact@cartonomie.fr directly.",
        direct_email: "Or email us directly at",
      };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.email || !form.message || form.message.length < 10) {
      setError(t.error);
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t.success_title);
    } catch (err) {
      const msg = err?.response?.data?.detail || t.error;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-white" data-testid="contact-page">
        <Seo title={isFr ? "Contact" : "Contact"} description={t.subtitle} />
        <div className="max-w-xl mx-auto px-6 lg:px-10 pt-24 pb-20 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-3">{t.success_title}</h1>
          <p className="text-sm text-slate-600 mb-8">{t.success_body}</p>
          <button
            data-testid="contact-send-another"
            onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "bug", message: "" }); }}
            className="px-4 py-2 text-xs font-semibold rounded-xl border border-slate-300 text-slate-700 hover:border-[#E11D48] hover:text-[#E11D48] transition"
          >
            {t.another}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Seo title={isFr ? "Contact" : "Contact"} description={t.subtitle} />

      <div className="max-w-2xl mx-auto px-6 lg:px-10 pt-16 pb-20">
        <span className="text-[10px] tracking-[0.18em] text-slate-400 font-semibold">{t.eyebrow}</span>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-3 mb-4">
          {t.title}
        </h1>
        <p className="text-sm text-slate-600 leading-relaxed mb-10">{t.subtitle}</p>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">{t.name}</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.name_ph}
                data-testid="contact-name-input"
                name="contact-name"
                className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:border-slate-900 focus:outline-none"
                maxLength={100}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">{t.email} *</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.email_ph}
                data-testid="contact-email-input"
                className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:border-slate-900 focus:outline-none"
                maxLength={150}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">{t.subject}</label>
            <select
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="contact-subject-select"
              className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:border-slate-900 focus:outline-none bg-white"
            >
              {SUBJECTS.map((s) => (
                <option key={s.value} value={s.value}>{isFr ? s.label_fr : s.label_en}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">{t.message} *</label>
            <textarea
              required
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t.message_ph}
              data-testid="contact-message-input"
              rows={7}
              minLength={10}
              maxLength={3000}
              className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:border-slate-900 focus:outline-none resize-none"
            />
            <div className="text-[10px] text-slate-400 mt-1 text-right">{form.message.length}/3000</div>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-50 text-rose-700 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit-btn"
            className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
            {loading ? t.sending : t.send}
          </button>
        </form>

        <div className="mt-12 pt-8 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
          <Mail className="w-4 h-4" />
          {t.direct_email}{" "}
          <a href="mailto:contact@cartonomie.fr" className="text-[#E11D48] font-semibold underline">contact@cartonomie.fr</a>
        </div>
      </div>
    </div>
  );
}
